import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Word from '../RenderStroyComponent/Word'
import { ChevronIcon } from 'containers/icons/icons'
import { StoryWord, WordObject } from 'types/wordDictionaryTypes'
import OneLineContent from './OneLineContent'
import { AttachedSentences, splitIntoParagraphs } from 'utils/storySentences'
import { OccurrenceSense } from 'utils/wordSenses'
import { useChineseLang } from '../../i18n/chinese'
import {
  sentenceTranslationText,
  storyHasLocalizedContent,
  storyParagraphText,
  storyParagraphTexts,
} from '../../i18n/localizedContent'

interface Story {
  storyContent: WordObject[]
  newWordsArray?: StoryWord[]
  learningWords?: StoryWord[]
  growingWords?: StoryWord[]
  developingWords?: StoryWord[]
  masteredWords?: StoryWord[]
  oldWords?: string[]
  newWord1?: string
  newWord2?: string
  englishContent?: string[]
  /** API overlay (docs/plans/localization.md): the translation in the learner's language. */
  contentLanguage?: string | null
  localizedContent?: (string | null)[]
  sentenceTranslations?: { paragraph: number; text: string; en: string; tr?: string | null }[] | null
}

interface ILeftStoryProps {
  /** Legacy "Show English Translation" box after the story (recording modal). */
  showTranslate: boolean
  /**
   * Reader English mode, driven by the toolbar toggle. With `sentences`, each
   * Chinese sentence gets its own row with its English beneath; without, each
   * paragraph gets its English beneath it.
   */
  showEnglish?: boolean
  sentences?: AttachedSentences | null
  story: Story
  handleClickWord?: (e: React.MouseEvent, wordObj: WordObject, sentence: WordObject[], index: string) => void
  activeIndex?: string
  chineseWordFontSize?: {
    storyContent?: string
    pinyinValue?: string
  }
  showPinYin: boolean
  /** DOM word id to the sense that occurrence has in its sentence (attachWordSenses). */
  wordSenses?: Map<string, OccurrenceSense> | null
}

const LeftStory: React.FC<ILeftStoryProps> = ({
  handleClickWord,
  story,
  activeIndex,
  chineseWordFontSize,
  showTranslate,
  showPinYin,
  showEnglish = false,
  sentences = null,
  wordSenses = null,
}) => {
  const [showMessage, setShowMessage] = useState(-1)
  const [showChinese, setShowChinese] = useState(-1)
  const [showTranslation, setShowTranslation] = useState(false)
  const zhLang = useChineseLang()
  const { t } = useTranslation('reader')

  const paragraphs = useMemo(() => splitIntoParagraphs(story.storyContent), [story.storyContent])

  const renderWord = (p: number, index: number) => (
    <OneLineContent
      key={index}
      iterator={`WORD-${p}`}
      wordObj={paragraphs[p][index]}
      WIndex={index}
      contentDetail={paragraphs[p] || []}
      story={story}
      showPinYin={showPinYin}
      activeIndex={activeIndex}
      handleClickWord={handleClickWord}
      chineseWordFontSize={chineseWordFontSize}
      textPinyin={wordSenses?.get(`WORD-${p}${index}`)?.textPinyin}
    />
  )

  const englishStyle = {
    fontSize: `max(15px, calc(${chineseWordFontSize?.storyContent || '24px'} * 0.6))`,
  }

  if (showEnglish) {
    return (
      <div className='w-col w-col-12 content-left padding_remove_div section_story_container'>
        <div
          style={{ fontSize: chineseWordFontSize?.storyContent, lineHeight: 'initial', letterSpacing: '0px', marginBottom: '5px' }}
          className='story-content story-event-select story_content_full_div story-english-mode'
          lang={zhLang}
        >
          {sentences
            ? sentences.rows.map((row) => (
                <div key={row.sentence} className={`sentence-row${row.startsParagraph ? ' sentence-row--paragraph' : ''}`}>
                  <div className='word_wrapper'>{row.words.map(({ p, i }) => renderWord(p, i))}</div>
                  {(() => {
                    const translation = sentenceTranslationText(
                      story.sentenceTranslations?.[row.sentence] ?? { en: row.en },
                      story.contentLanguage,
                    )
                    return (
                      <p className='sentence-row-english' lang={translation.lang} style={englishStyle}>
                        {translation.text}
                      </p>
                    )
                  })()}
                </div>
              ))
            : paragraphs.map((paragraph, p) =>
                paragraph.length ? (
                  <div key={p} className='sentence-row sentence-row--paragraph'>
                    <div className='word_wrapper'>{paragraph.map((_, i) => renderWord(p, i))}</div>
                    {(() => {
                      const translation = storyParagraphText(story, p)
                      return translation.text ? (
                        <p className='sentence-row-english' lang={translation.lang} style={englishStyle}>
                          {translation.text}
                        </p>
                      ) : null
                    })()}
                  </div>
                ) : null,
              )}
        </div>
      </div>
    )
  }

  return (
    <div className='w-col w-col-12 content-left padding_remove_div section_story_container'>
      <div
        style={{
          fontSize: chineseWordFontSize?.storyContent,
          lineHeight: 'initial',
          letterSpacing: '0px',
          marginBottom: '5px',
        }}
        className='story-content story-event-select story_content_full_div'
        lang={zhLang}
      >
        {paragraphs.map((sentence, sIndex) => (
          <div
            key={sIndex}
            className={sIndex === showChinese ? 'sentance-hover' : ''}
            onMouseEnter={() => setShowMessage(sIndex)}
            onMouseLeave={() => setShowMessage(-1)}
          >
            <div className='word_wrapper'>
              {sentence?.map((wordObj, index) => (
                <OneLineContent
                  key={index}
                  iterator={`WORD-${sIndex}`}
                  wordObj={wordObj}
                  WIndex={index}
                  contentDetail={sentence || []}
                  story={story}
                  showPinYin={showPinYin}
                  activeIndex={activeIndex}
                  handleClickWord={handleClickWord}
                  chineseWordFontSize={chineseWordFontSize}
                  textPinyin={wordSenses?.get(`WORD-${sIndex}${index}`)?.textPinyin}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {showTranslate && (
        <div className='translation-box'>
          <div className='translation-button' onClick={() => setShowTranslation(!showTranslation)}>
            {storyHasLocalizedContent(story)
              ? showTranslation
                ? t('translationBox.hide')
                : t('translationBox.show')
              : showTranslation
                ? t('translationBox.hideEnglish')
                : t('translationBox.showEnglish')}
            <ChevronIcon isUp={showTranslation} />
          </div>

          {showTranslation && story.englishContent && story.englishContent?.length > 0 && (
            <div
              style={{
                fontSize: chineseWordFontSize?.storyContent,
                lineHeight: '1.5',
                letterSpacing: '1.5px',
              }}
              className='story-content story-event-select m-b-10'
            >
              {storyParagraphTexts(story).map((paragraph, index) => (
                <div
                  key={index}
                  className={`translation-text ${index === showMessage ? 'sentance-hover' : ''}`}
                  onMouseEnter={() => setShowChinese(index)}
                  onMouseLeave={() => setShowChinese(-1)}
                  lang={paragraph.lang}
                >
                  {paragraph.text}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* <div className='mob_scroll_bottom_space'></div> */}
    </div>
  )
}

export { Word }
export default LeftStory
